<template>
  <v-container>
    <v-card title="Data Export">
      <v-card-text>
        <v-data-table-server
          v-model="selected"
          v-model:items-per-page="pagination.limit"
          :items="teams"
          :headers="headersTeam"
          :items-length="count"
          :loading="loading"
          :search="pagination.q"
          item-key="id"
          show-select
          return-object
          @update:options="handleLimitChange">
          <template #item.team="{ item }">
            {{ item.teamCriteria?.team?.name }}
          </template>
          <template #item.title="{ item }">
            {{ entryFieldLookup(item, 'Title') }}
          </template>
          <template #item.tags="{ item }">
            {{ getTags(item) }}
          </template>
          <template #item.description="{ item }">
            {{ getDescription(item) }}
          </template>
          <template #item.logline="{ item }">
            {{ entryFieldLookup(item, 'Log Line') }}
          </template>
          <template #item.filmUrl="{ item }">
            {{ item.screeningCopy ? item.screeningCopy?.url : item.movie?.url }}
          </template>
          <template #item.coverUrl="{ item }">
            {{ item.screenshot?.url }}
          </template>
          <template #item.year="{ item }"> 2025 </template>
          <template #item.runtime="{ item }">
            {{ entryFieldLookup(item, 'Running Time') }}
          </template>
          <template #item.rating="{ item }">
            {{ entryFieldLookup(item, 'Rating') }}
          </template>
          <template #item.genre="{ item }">
            {{ entryFieldLookup(item, 'Genre') }}
          </template>
          <template #item.facebook="{ item }">
            {{
              item.teamCriteria?.team?.facebook
                ? 'https://www.facebook.com/' + item.teamCriteria?.team?.facebook
                : ''
            }}
          </template>
          <template #item.twitter="{ item }">
            {{
              item.teamCriteria?.team?.twitter
                ? 'https://www.x.com/' + item.teamCriteria?.team?.twitter
                : ''
            }}
          </template>
          <template #item.instagram="{ item }">
            {{
              item.teamCriteria?.team?.instagram
                ? 'https://www.instagram.com/' + item.teamCriteria?.team?.instagram
                : ''
            }}
          </template>
          <template #item.director="{ item }">
            {{ castCrewLookUp(item, 'Director') }}
          </template>
          <template #item.screenwriter="{ item }">
            {{ castCrewLookUp(item, 'Writer') }}
          </template>
          <template #item.producer="{ item }"> {{ castCrewLookUp(item, 'Producer') }} </template>
          <template #item.executiveProducer="{ item }">
            {{ castCrewLookUp(item, 'Executive Producer') }}
          </template>
          <template #item.coProducer="{ item }">
            {{ castCrewLookUp(item, 'Co-Producer') }}
          </template>
          <template #item.filmmaker="{ item }"> </template>
          <template #item.cast="{ item }"> {{ castCrewLookUp(item, 'Actor/Actress') }} </template>
          <template #item.cinematographer="{ item }">
            {{ castCrewLookUp(item, 'Director of Photography') }}
          </template>
          <template #item.editor="{ item }"> {{ castCrewLookUp(item, 'Editor') }} </template>
          <template #item.animator="{ item }"> {{ castCrewLookUp(item, 'Animator') }} </template>
          <template #item.productionDesign="{ item }">
            {{ castCrewLookUp(item, 'Production Designer') }}
          </template>
          <template #item.composer="{ item }">
            {{ castCrewLookUp(item, 'Composer/Musician') }}
          </template>
          <template #item.soundDesign="{ item }">
            {{ castCrewLookUp(item, 'Sound Design') }}
          </template>
          <template #item.music="{ item }">
            {{ castCrewLookUp(item, 'Composer/Musician') }}
          </template>
          <template v-slot:top>
            <v-text-field
              v-model="pagination.q"
              class="ma-2"
              density="compact"
              placeholder="Search team..."
              hide-details></v-text-field>
            <v-btn color="primary" @click="exportToCSV(headersTeam, teams)">Export to CSV</v-btn>
          </template>
        </v-data-table-server>
      </v-card-text>
      <v-card-actions v-if="selected.length > 0"> </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { VDataTableServer } from 'vuetify/components';
import { IHeaderDTO, IPaginationDTO, ISubmissionDTO, PaginationDTO, SubmissionDTO } from '@/dtos';
import { SNACKSTATUS } from '@/stores/util';
import { useAdminSubmissionStore } from '@/stores/modules/admin';
import { exportToCSV } from '@/helpers';

const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
interface Props {
  branchId: string;
  yearId: string;
}
const props = defineProps<Props>();
const branchId = computed(() => props.branchId);
const yearId = computed(() => props.yearId);
const submissionStore = useAdminSubmissionStore();
const pagination = ref<IPaginationDTO>(new PaginationDTO());
const { data, isLoading } = submissionStore.getAllSubmissions(branchId, yearId, pagination);
const loading = computed(() => isLoading.value);
const teams = computed(() => data.value?.data ?? []);
const count = computed(() => data.value?.count ?? 0);
const selected = ref<ISubmissionDTO[]>([]);
const headersTeam: (IHeaderDTO & { exportValue?: (item: ISubmissionDTO) => string })[] = [
  {
    title: 'Title',
    key: 'title',
    sortable: false,
    exportValue: (item) => entryFieldLookup(item, 'Title'),
  },
  { title: 'Tags', key: 'tags', sortable: false, exportValue: (item) => getTags(item) },
  {
    title: 'Team',
    key: 'team',
    sortable: false,
    exportValue: (item) => item.teamCriteria?.team?.name ?? '',
  },
  {
    title: 'Description',
    key: 'description',
    sortable: false,
    exportValue: (item) => getDescription(item),
  },
  {
    title: 'Logline',
    key: 'logline',
    sortable: false,
    exportValue: (item) => entryFieldLookup(item, 'Log Line'),
  },
  {
    title: 'Url To Film',
    key: 'filmUrl',
    sortable: false,
    exportValue: (item) => item.screeningCopy?.url ?? item.movie?.url ?? '',
  },
  {
    title: 'Url To Cover Image',
    key: 'coverUrl',
    sortable: false,
    exportValue: (item) => item.screenshot?.url ?? '',
  },
  {
    title: 'Url To Stil Image',
    key: 'stilUrl',
    sortable: false,
    exportValue: (item) => item.screenshot?.url ?? '',
  }, // duplicated coverUrl slot in your template
  { title: 'Year', key: 'year', sortable: false, exportValue: () => '2025' },
  {
    title: 'Runtime',
    key: 'runtime',
    sortable: false,
    exportValue: (item) => entryFieldLookup(item, 'Running Time'),
  },
  {
    title: 'Rating',
    key: 'rating',
    sortable: false,
    exportValue: (item) => entryFieldLookup(item, 'Rating'),
  },
  {
    title: 'Genre',
    key: 'genre',
    sortable: false,
    exportValue: (item) => entryFieldLookup(item, 'Genre'),
  },
  {
    title: 'Facebook',
    key: 'facebook',
    sortable: false,
    exportValue: (item) =>
      item.teamCriteria?.team?.facebook
        ? `https://www.facebook.com/${item.teamCriteria.team.facebook}`
        : '',
  },
  {
    title: 'Twitter',
    key: 'twitter',
    sortable: false,
    exportValue: (item) =>
      item.teamCriteria?.team?.twitter ? `https://www.x.com/${item.teamCriteria.team.twitter}` : '',
  },
  {
    title: 'Instagram',
    key: 'instagram',
    sortable: false,
    exportValue: (item) =>
      item.teamCriteria?.team?.instagram
        ? `https://www.instagram.com/${item.teamCriteria.team.instagram}`
        : '',
  },
  {
    title: 'Director',
    key: 'director',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Director'),
  },
  {
    title: 'Screenwriter',
    key: 'screenwriter',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Writer'),
  },
  {
    title: 'Producer',
    key: 'producer',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Producer'),
  },
  {
    title: 'Executive Producer',
    key: 'executiveProducer',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Executive Producer'),
  },
  {
    title: 'Co-Producer',
    key: 'coProducer',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Co-Producer'),
  },
  { title: 'Filmmaker', key: 'filmmaker', sortable: false, exportValue: () => '' }, // your template had no content here
  {
    title: 'Cast',
    key: 'cast',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Actor/Actress'),
  },
  {
    title: 'Cinematographer',
    key: 'cinematographer',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Director of Photography'),
  },
  {
    title: 'Editor',
    key: 'editor',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Editor'),
  },
  {
    title: 'Animator',
    key: 'animator',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Animator'),
  },
  {
    title: 'Production Design',
    key: 'productionDesign',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Production Designer'),
  },
  {
    title: 'Composer',
    key: 'composer',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Composer/Musician'),
  },
  {
    title: 'Sound Design',
    key: 'soundDesign',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Sound Design'),
  },
  {
    title: 'Music',
    key: 'music',
    sortable: false,
    exportValue: (item) => castCrewLookUp(item, 'Composer/Musician'),
  },
  { title: '', key: 'action', sortable: false, exportValue: () => '' }, // table action buttons
];

const handleLimitChange = (value: VDataTableServer) => {
  pagination.value.buildPagination(value);
};

const entryFieldLookup = (submission: ISubmissionDTO, field: string) => {
  const submissionEntryForm = submission.submissionEntryForm;
  if (!submissionEntryForm) return '';
  const foundField = submissionEntryForm.fields.find((f) => f.entryField.name === field);
  return foundField ? foundField.value : '';
};

const castCrewLookUp = (submission: ISubmissionDTO, field: string) => {
  const castCrew = submission.castCrew;
  if (!castCrew) return '';

  const matches = castCrew.filter((f) => f.castCrew?.name === field);

  return matches.length ? matches.map((m) => `${m.first_name} ${m.last_name}`).join('; ') : '';
};

const getTags = (submission: ISubmissionDTO) => {
  if (!submission.teamCriteria?.criteriaRelations) return '';
  return submission.teamCriteria?.criteriaRelations
    .map((t) => t.criteria?.group?.group_name)
    .join(', ');
};

const getDescription = (submission: ISubmissionDTO) => {
  if (!submission.teamCriteria?.criteriaRelations) return '';
  return submission.teamCriteria?.criteriaRelations.map((t) => t.criteria?.description).join(', ');
};
</script>
